<template>
    <div data-recaptcha></div>
</template>

<script>
let gRecaptchaApiLoaded;

import { whenConsented } from "../../helper/whenConsented";


export default {

    name: "recaptcha",

    props:
    {
        deferred:
        {
            type: Boolean,
            default: false
        }
    },

    data()
    {
        return {
            version: App.config.global.googleRecaptchaVersion,
            apiKey: App.config.global.googleRecaptchaApiKey,
            recaptchaInitiated: false,
            loadPromise: null,
            deferredForm: null
        };
    },

    mounted()
    {
        this.$nextTick(() =>
        {
            this.$el.ensureRecaptcha = () => this.ensureRecaptcha();

            if (this.deferred)
            {
                this.deferredForm = this.$el.closest("form");

                if (this.deferredForm)
                {
                    this.deferredForm.addEventListener("focusin", this.ensureRecaptcha, { once: true });
                    this.deferredForm.addEventListener("pointerdown", this.ensureRecaptcha, { once: true, passive: true });
                }
            }
            else
            {
                this.ensureRecaptcha();
            }

            document.addEventListener("consent-change", () => this.ensureRecaptchaAfterConsent());
        });
    },

    beforeDestroy()
    {
        if (this.deferredForm)
        {
            this.deferredForm.removeEventListener("focusin", this.ensureRecaptcha);
            this.deferredForm.removeEventListener("pointerdown", this.ensureRecaptcha);
        }
    },

    computed: {
        consentGroupKey() {
            const consentGroup = App.config.global.googleRecaptchaConsentGroup;

            return  consentGroup ?  `${consentGroup}.reCaptcha` : 'media.reCaptcha'
        }
    },

    methods:
    {
        ensureRecaptcha()
        {
            if (this.loadPromise)
            {
                return this.loadPromise;
            }

            this.loadPromise = new Promise((resolve, reject) =>
            {
                whenConsented(
                    this.consentGroupKey,
                    () => this.createScript().then(() => this.initializeV3()).then(resolve).catch(reject),
                    () => reject(new Error("reCAPTCHA consent was not granted")),
                    true
                );
            }).catch(error =>
            {
                this.loadPromise = null;
                throw error;
            });

            return this.loadPromise;
        },

        ensureRecaptchaAfterConsent()
        {
            if (!this.deferred)
            {
                this.ensureRecaptcha();
            }
        },

        createScript()
        {
            if (!this.apiKey || window.grecaptcha)
            {
                return Promise.resolve();
            }

            if (!gRecaptchaApiLoaded && !this.recaptchaInitiated)
            {
                gRecaptchaApiLoaded = new Promise((resolve, reject) =>
                {
                    const script = document.createElement("script");
                    let scriptSource;

                    if (this.version === 3)
                    {
                        scriptSource = `https://www.google.com/recaptcha/api.js?render=${this.apiKey}`;
                    }
                    else
                    {
                        scriptSource = "https://www.google.com/recaptcha/api.js";
                    }

                    script.type = "text/javascript";
                    script.id = "google-recaptcha-api";
                    script.src = scriptSource;

                    script.addEventListener("load", () => resolve(script), false);
                    script.addEventListener("error", () => reject(script), false);

                    document.body.appendChild(script);
                });
            }

            return gRecaptchaApiLoaded;
        },

        initializeV3()
        {
            if (!window.grecaptcha || this.recaptchaInitiated)
            {
                return Promise.resolve();
            }

            this.recaptchaInitiated = true;

            return new Promise(resolve =>
            {
                window.grecaptcha.ready(() =>
                {
                    if (this.version !== 3)
                    {
                        this.$el.dataset.recaptcha = window.grecaptcha.render(
                            this.$el,
                            {
                                sitekey: this.apiKey,
                                size: "invisible",
                                badge: "bottomright",
                                callback: this.recaptchaCallback.bind(this)
                            }
                        );
                    }

                    resolve();
                });
            });
        },

        recaptchaCallback(response)
        {
            this.$el.querySelector("[name=\"g-recaptcha-response\"]")
                .dispatchEvent(
                    new CustomEvent("recaptcha-response", { response: response })
                );
        }
    }
}
</script>
